import type { ReductionEvidenceListModel } from "@/api/cle-api";
import { CleApiErrorText } from "@/components/cle-api/CleApiErrorText";
import DocumentIcon from "@/components/icons/DocumentIcon";
import { TableBody, TableHead, TableRoot } from "@/components/Table/TableBasic";

import { DocumentPaginationControls } from "./DocumentPaginationControls";
import { StatusBadge } from "./StatusBadge";
import {
  formatEvidenceStatus,
  getEvidenceStatusVariant,
} from "./utils/evidenceStatus";
import { formatBoolean, formatDate } from "./utils/formatters";
import type { MockPagination } from "./types";

interface ReductionsTableProps {
  reductions: ReductionEvidenceListModel[];
  isLoading: boolean;
  error: Error | null;
  onSelectReduction: (reductionId: string) => void;
  pagination: MockPagination;
  showPagination?: boolean;
  onPreviousPage?: () => void;
  onNextPage?: () => void;
}

export const ReductionsTable = ({
  reductions,
  isLoading,
  error,
  onSelectReduction,
  pagination,
  showPagination = true,
  onPreviousPage,
  onNextPage,
}: ReductionsTableProps) => (
  <>
    <TableRoot>
      <TableHead>
        <th>Dokument</th>
        <th>Lektioner</th>
        <th>Uploadet den</th>
        <th>Rapporteret</th>
        <th>Status</th>
        <th>Handling</th>
      </TableHead>
      <TableBody>
        {isLoading && (
          <tr>
            <td colSpan={6} className="text-muted">
              Henter reduktioner ...
            </td>
          </tr>
        )}
        {error && (
          <tr>
            <td colSpan={6}>
              <CleApiErrorText
                error={error}
                fallback="Kunne ikke hente reduktioner."
              />
            </td>
          </tr>
        )}
        {!isLoading &&
          !error &&
          reductions.map((reduction) => (
            <tr
              key={reduction.id}
              className="cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() => onSelectReduction(reduction.id)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSelectReduction(reduction.id);
                }
              }}
            >
              <td>
                <div className="d-flex align-items-center gap-2">
                  <DocumentIcon height={16} />
                  {reduction.fileName ?? "-"}
                </div>
              </td>
              <td>{reduction.numberOfLessons}</td>
              <td>{formatDate(reduction.createdAt)}</td>
              <td>{formatBoolean(reduction.isReported)}</td>
              <td>
                <StatusBadge
                  status={formatEvidenceStatus(reduction.status)}
                  variant={getEvidenceStatusVariant(reduction.status)}
                />
              </td>
              <td>
                <button
                  id={`efteruddannelse-reduction-${reduction.id}`}
                  type="button"
                  className="btn btn--action"
                  onClick={(event) => {
                    event.stopPropagation();
                    onSelectReduction(reduction.id);
                  }}
                >
                  Vis
                </button>
              </td>
            </tr>
          ))}
        {!isLoading && !error && !reductions.length && (
          <tr>
            <td colSpan={6} className="text-muted">
              Ingen reduktioner
            </td>
          </tr>
        )}
      </TableBody>
    </TableRoot>
    <div className="d-flex justify-content-end mt-2">
      <DocumentPaginationControls
        pagination={pagination}
        showPagination={showPagination}
        onPreviousClick={onPreviousPage}
        onNextClick={onNextPage}
      />
    </div>
  </>
);
